import React, { ReactElement, RefCallback, RefObject } from 'react';

import { Placement } from '@popperjs/core';
import PropTypes from 'prop-types';

import { Container as ContainerType } from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';

import { PLACEMENTS } from './consts';
import useOverlay, { UseOverlayProps } from './hooks/useOverlay';

type OverlayDeprecatedProps<TTarget extends HTMLElement> = Omit<
  UseOverlayProps<TTarget>,
  'target'
> & {
  target?: RefObject<TTarget>;
  container?: ContainerType;
  children: ({
    props,
    arrowProps,
    placement,
  }: {
    props: { ref: RefCallback<HTMLElement> };
    arrowProps: { ref: RefCallback<HTMLElement> };
    placement: Placement;
  }) => ReactElement;
};

/**
 * React Overlay wrapper component for useOverlay.
 * DEPRECATED:  Use Overlay instead or useOverlay directly.
 */
const OverlayDeprecated = <TTarget extends HTMLElement>({
  children,
  show: showProp,
  container,
  placement: placementProp,
  ...props
}: OverlayDeprecatedProps<TTarget>) => {
  const { show, placement, overlayProps, arrowProps } = useOverlay({
    placement: placementProp,
    show: showProp,
    ...props,
  });

  return show ? (
    <Portal container={container}>
      {children({
        props: overlayProps,
        arrowProps,
        placement,
      })}
    </Portal>
  ) : null;
};

OverlayDeprecated.displayName = 'OverlayDeprecated';

OverlayDeprecated.defaultProps = {
  placement: PLACEMENTS.BOTTOM,
  container: document.body,
};

OverlayDeprecated.propTypes = {
  /**
   * whether the popover is showed or not. should be sent only for
   * controlled elements
   */
  show: PropTypes.bool,
  /**
   * callback to be called on show
   */
  onShow: PropTypes.func,
  /**
   * callback to be called on hide
   */
  onHide: PropTypes.func,
  /**
   * delay before show in milliseconds
   */
  delayShow: PropTypes.number,
  /**
   * delay before hide in milliseconds
   */
  delayHide: PropTypes.number,
  /** overlay position according to Overlay placements. */
  placement: PropTypes.oneOf(Object.values(useOverlay.PLACEMENTS)),
  /** target element ref (get/set).  */
  target: PropTypes.object,
  /** overlay ref to overlay dom element. */
  overlayRef: PropTypes.any,
  /** Specify whether the overlay should trigger `onHide` when the user clicks outside the overlay. */
  rootClose: PropTypes.bool,
  /** Dependency to pass to usePopper's useLayoutEffect hook */
  layoutDependency: PropTypes.any,
  /**
   * A DOM Element, Ref to an element, or function that returns either. The `container` will have the Portal children
   * appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

  /**
   * A render prop that returns an element to overlay and position. See
   * the [react-popper documentation](https://github.com/FezVrasta/react-popper#children) for more info.
   *
   * @type {Function ({
   *   show: boolean,
   *   placement: Placement,
   *   outOfBoundaries: ?boolean,
   *   props: {
   *     ref: (?HTMLElement) => void,
   *     style: { [string]: string | number },
   *     aria-labelledby: ?string
   *   },
   *   arrowProps: {
   *     ref: (?HTMLElement) => void,
   *     style: { [string]: string | number },
   *   },
   * }) => React.Element}
   */
  children: PropTypes.func.isRequired,
};

export default OverlayDeprecated;
